import { Icon, spacing, Text } from '@scality/core-ui';
import { Box, Button, ButtonGroup } from '@scality/core-ui/dist/next';
import { Fragment, useMemo, useState } from 'react';
import { ChartCard } from '../../chat-messages/ChartWrapper';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { NoData } from '../NoData';
import { Legend } from './Legend';
import { useColorByNodeKey, useObservedWidthPx } from './NestedProportionBar.hooks';
import type {
  BoxFigure,
  BoxOptions,
  NestedProportionBarProps,
  NodeId,
  NodePath,
  ProportionNode,
} from './NestedProportionBar.types';
import {
  buildProportionTable,
  findFoldedIds,
  findNodesAlongPath,
  flatProportionNodeKeys,
  legendText,
  nodeIdOf,
  ownMinWidthFor,
} from './NestedProportionBar.utils';
import { ProportionBox } from './ProportionBox';

/**
 * A hierarchical breakdown of one total: each node is a box whose width is proportional to its
 * parent and which *contains* its children. That containment is the point, and it is what a
 * stacked `BarChart` loses — side by side, segments only say "these add up"; nested, they say
 * "this one is part of that one".
 *
 * Composed from core-ui primitives (`Box`, `Text`, `Tooltip`) rather than a core-ui component, so
 * it lives here until it earns a place in the library.
 */
export const NestedProportionBar: AIToolComponent<NestedProportionBarProps> = ({
  params: { title, root, unit, percentageBase = 'total', legend = [] },
}) => {
  const colors = useColorByNodeKey(useMemo(() => (root ? flatProportionNodeKeys(root) : []), [root]));
  const [zoomedPath, setZoomedPath] = useState<NodePath | null>(null);
  const [boxFigure, setBoxFigure] = useState<BoxFigure>(percentageBase);

  // A zero total has no proportions to draw, and an empty range is a normal answer.
  if (!root || root.value <= 0) {
    return (
      <ChartCard>
        <NoData title={title} message="Nothing to break down over this time range." />
      </ChartCard>
    );
  }

  const applies = legendText(legend);

  const zoomedNodes = zoomedPath ? findNodesAlongPath(root, zoomedPath) : [];
  const displayedRoot = zoomedNodes[zoomedNodes.length - 1] ?? root;
  const ownMinWidth = ownMinWidthFor(boxFigure, unit);

  return (
    <ChartCard
      title={title}
      buildExportTable={title ? () => buildProportionTable({ name: title, root, unit }) : undefined}
    >
      {(view) => (
        <Box display="flex" flexDirection="column" gap={spacing.f8} minWidth={0}>
          {title ? (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={spacing.f8} minWidth={0}>
              <Text variant="ChartTitle">{title}</Text>
              {view.actions}
            </Box>
          ) : null}
          {/* Reversed: the toggle keeps the top right, the breadcrumb is what wraps below. */}
          <Box display="flex" flexDirection="row-reverse" alignItems="center" gap={spacing.f8} flexWrap="wrap">
            <ButtonGroup
              aria-label="Each box shows"
              value={boxFigure}
              onChange={(figure) => setBoxFigure(figure as BoxFigure)}
            >
              {BOX_FIGURES.map((figure) => (
                <Button key={figure} value={figure} size="inline" label={BOX_FIGURE_LABELS[figure]} />
              ))}
            </ButtonGroup>
            {zoomedNodes.length > 1 ? <ZoomBreadcrumb nodes={zoomedNodes} onZoomOutTo={setZoomedPath} /> : null}
          </Box>
          <MeasuredBar
            ariaLabel={`Breakdown of ${root.label}${applies ? ` — ${applies}` : ''}`}
            displayedRoot={displayedRoot}
            displayedRootPath={zoomedNodes.length > 0 ? zoomedNodes.map((node) => node.key) : [root.key]}
            displayedRootParentValue={zoomedNodes[zoomedNodes.length - 2]?.value ?? displayedRoot.value}
            boxOptions={{
              total: root.value,
              unit,
              figure: boxFigure,
              colors,
              ownMinWidth,
              onZoomInto: setZoomedPath,
            }}
          />
          {legend.length > 0 ? <Legend legend={legend} /> : null}
        </Box>
      )}
    </ChartCard>
  );
};

const BOX_FIGURES: BoxFigure[] = ['total', 'parent', 'value'];

const BOX_FIGURE_LABELS: Record<BoxFigure, string> = {
  total: '% of total',
  parent: '% of parent',
  value: 'Values',
};

/**
 * The bar itself, measuring its own width: the chart card renders the widget twice while enlarged,
 * and each copy folds by the room it has, not by the other's.
 */
const MeasuredBar = ({
  ariaLabel,
  displayedRoot,
  displayedRootPath,
  displayedRootParentValue,
  boxOptions,
}: {
  ariaLabel: string;
  displayedRoot: ProportionNode;
  displayedRootPath: NodePath;
  displayedRootParentValue: number;
  boxOptions: Omit<BoxOptions, 'foldedIds'>;
}) => {
  const [observeWidth, barWidthPx] = useObservedWidthPx();
  const foldedIds =
    barWidthPx !== null
      ? findFoldedIds(displayedRoot, displayedRootPath, barWidthPx, boxOptions.ownMinWidth)
      : new Set<NodeId>();

  return (
    <Box ref={observeWidth} role="figure" aria-label={ariaLabel} minWidth={0}>
      <ProportionBox
        node={displayedRoot}
        path={displayedRootPath}
        parentValue={displayedRootParentValue}
        widthShare={1}
        depth={0}
        options={{ ...boxOptions, foldedIds }}
      />
    </Box>
  );
};

/** Every node but the last zooms back out to it; the first, the root, clears the zoom. */
const ZoomBreadcrumb = ({
  nodes,
  onZoomOutTo,
}: {
  nodes: ProportionNode[];
  onZoomOutTo: (path: NodePath | null) => void;
}) => (
  <Box display="flex" alignItems="center" gap={spacing.f4} flexWrap="wrap" marginRight="auto">
    {nodes.map((node, index) => {
      const isRoot = index === 0;
      const isZoomedNode = index === nodes.length - 1;
      const nodePath = nodes.slice(0, index + 1).map((ancestor) => ancestor.key);
      return isZoomedNode ? (
        <Text key={nodeIdOf(nodePath)}>{node.label}</Text>
      ) : (
        <Fragment key={nodeIdOf(nodePath)}>
          <Button
            variant="outline"
            size="inline"
            icon={<Icon name="Search" />}
            label={node.label}
            tooltip={{ overlay: `Zoom out to ${node.label}` }}
            onClick={() => onZoomOutTo(isRoot ? null : nodePath)}
          />
          <Text color="textSecondary">›</Text>
        </Fragment>
      );
    })}
  </Box>
);

NestedProportionBar.description =
  'Displays a hierarchical breakdown of one total as nested boxes: each box is as wide as its ' +
  'share of its parent and contains its children. Use it when one value is *inside* another — ' +
  'total capacity containing used, used containing stored, stored containing unique — which a ' +
  'stacked bar cannot say. Every value is absolute and in one unit across the whole tree; the ' +
  'boxes print percentages, so do not pre-compute them. Colours are not yours to send: each box ' +
  'takes one from the same chart palette the line and bar charts use. Always fill `legend` with ' +
  'what the values apply to — the ring(s) or node(s) they were summed over, or the deployment(s) ' +
  'for a platform-wide total — since a total says nothing without it. Keep it to four levels and ' +
  'short labels — a long one is truncated to fit its box, and its tooltip keeps the whole ' +
  'name. Name the space nothing measures directly rather than leaving a gap: give it the value ' +
  'its parent has left — and give that one a `description`, since no metric names it. For parts ' +
  'that merely add up to a total, use BarChart instead.';

NestedProportionBar.schema = {
  type: 'object',
  properties: {
    title: { type: 'string', description: 'Optional headline above the bar' },
    unit: { type: 'string', description: "Unit of every value in the tree, e.g. 'TB', 'objects'" },
    percentageBase: {
      type: 'string',
      enum: ['total', 'parent'],
      description:
        "Which share each box prints: 'total' (default) reads against the root, 'parent' against its own parent. Widths always follow the parent.",
    },
    legend: {
      type: 'array',
      items: { type: 'string' },
      description:
        "What the values apply to, read under the bar: the rings, nodes or deployments they were summed over — the finest of those, since a ring already implies its deployment. A deployment is written '(id)<install base id>' so the UI can resolve its name; end a long list with a '+N more' entry. Not a colour key: the boxes name themselves.",
    },
    root: { $ref: '#/definitions/node' },
  },
  required: ['root'],
  definitions: {
    node: {
      type: 'object',
      properties: {
        key: { type: 'string', description: 'Unique among its siblings' },
        label: { type: 'string', description: 'Short: one truncated line' },
        value: { type: 'number', description: 'Absolute value, in the tree-wide unit' },
        description: {
          type: 'string',
          description:
            'What the box holds, in a sentence, shown in its tooltip. Use it when the label alone leaves the reader guessing.',
        },
        children: { type: 'array', items: { $ref: '#/definitions/node' } },
      },
      required: ['key', 'label', 'value'],
    },
  },
};

NestedProportionBar.displayInPanel = true;
